import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, ShieldCheck, MessageCircle, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackLead, trackContact, trackInitiateCheckout } from '../utils/pixel';
export function LeadForm() {
  const {
    t,
    lang
  } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    phone: '',
    email: '',
    vehicleInterest: 'SUV',
    creditSituation: 'Not sure'
  });
  const [showExtended, setShowExtended] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [countdown, setCountdown] = useState(7);
  const [waOpened, setWaOpened] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const waOpenedRef = useRef(false);
  // Urgency timer — persisted to localStorage
  const [urgency, setUrgency] = useState(() => {
    const saved = localStorage.getItem('urgencyStart');
    if (saved) {
      const elapsed = Math.floor((Date.now() - parseInt(saved)) / 1000);
      const total = 2 * 3600 + 47 * 60 + 33; // 2h 47m 33s in seconds
      const remaining = Math.max(0, total - elapsed);
      return {
        hours: Math.floor(remaining / 3600),
        minutes: Math.floor(remaining % 3600 / 60),
        seconds: remaining % 60
      };
    }
    localStorage.setItem('urgencyStart', Date.now().toString());
    return {
      hours: 2,
      minutes: 47,
      seconds: 33
    };
  });
  // Check for duplicate on mount + restore saved data
  useEffect(() => {
    const last = localStorage.getItem('lastSubmission');
    if (last && Date.now() - parseInt(last) < 86400000) {
      setIsDuplicate(true);
      // Restore saved form data for personalized WA link
      const savedName = localStorage.getItem('leadName') || '';
      const savedVehicle = localStorage.getItem('leadVehicle') || 'SUV';
      setFormData(prev => ({
        ...prev,
        firstName: savedName,
        vehicleInterest: savedVehicle
      }));
    }
  }, []);
  // Urgency countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgency(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) return {
          hours: 0,
          minutes: 0,
          seconds: 0
        };
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Success countdown → open WhatsApp in new tab
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isSuccess && countdown > 0) {
      timer = setInterval(() => setCountdown(p => p - 1), 1000);
    } else if (isSuccess && countdown === 0 && !waOpenedRef.current) {
      openWhatsApp();
    }
    return () => clearInterval(timer);
  }, [isSuccess, countdown]);
  const getWhatsAppUrl = () => {
    const msg = t('wa.form').replace('{name}', formData.firstName).replace('{vehicle}', formData.vehicleInterest);
    return `https://wa.me/19548223252?text=${encodeURIComponent(msg)}`;
  };
  const openWhatsApp = () => {
    if (waOpenedRef.current) return;
    waOpenedRef.current = true;
    setWaOpened(true);
    trackContact('lead-form-whatsapp');
    const url = getWhatsAppUrl();
    const newWindow = window.open(url, '_blank');
    // If popup was blocked, show fallback after a short delay
    setTimeout(() => {
      if (!newWindow || newWindow.closed) {
        setShowFallback(true);
      }
    }, 1000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(p => ({
      ...p,
      [e.target.name]: e.target.value
    }));
  };
  const GOOGLE_SHEETS_WEBHOOK = 'https://script.google.com/macros/s/AKfycbx7_i8D-DrNU1CO_WZ7oakokGHodA6UL1EeQlxes07MufBhFLrqPh3XG4jaqY0PbycbsQ/exec';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Save to localStorage
    localStorage.setItem('lastSubmission', Date.now().toString());
    localStorage.setItem('leadName', formData.firstName);
    localStorage.setItem('leadVehicle', formData.vehicleInterest);
    // 1. Fire pixel
    trackLead({
      vehicle: formData.vehicleInterest,
      credit: formData.creditSituation
    });
    // 2. Send to Google Sheets (fire-and-forget, no-cors + form-urlencoded = no preflight)
    try {
      fetch(GOOGLE_SHEETS_WEBHOOK, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: new URLSearchParams({
          name: formData.firstName,
          phone: formData.phone,
          email: formData.email,
          vehicle: formData.vehicleInterest,
          credit: formData.creditSituation,
          language: lang,
          source: 'Landing Page - Mitchellthekiaboss Lead Form'
        }).toString()
      });
    } catch {}
    // 3. Show success after brief delay for perceived processing
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const pad = (n: number) => n.toString().padStart(2, '0');
  const inputClass = 'w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C41230] focus:ring-1 focus:ring-[#C41230]/50 transition-all placeholder:text-white/30';
  const labelClass = 'text-[11px] md:text-xs font-semibold text-white/50 uppercase tracking-wider';
  const UrgencyTimer = () => <div className="flex items-center justify-center gap-2 mb-5 text-white/40 text-xs" data-id="element-100">
      <Clock className="w-3 h-3" data-id="element-101" />
      <span data-id="element-102">{t('urgency.text')}</span>
      <span className="text-[#d4a853] font-mono font-semibold" data-id="element-103">
        {pad(urgency.hours)}:{pad(urgency.minutes)}:{pad(urgency.seconds)}
      </span>
    </div>;
  // ── DUPLICATE SCREEN ──
  if (isDuplicate) {
    return <section id="lead-form" className="py-16 md:py-24 bg-[#111111] text-white" data-id="element-104">
        <div className="container mx-auto px-5 md:px-6" data-id="element-105">
          <motion.div className="max-w-lg mx-auto bg-white/5 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-white/10 text-center" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} data-id="element-106">
            <div className="w-16 h-16 bg-[#C41230]/20 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-107">
              <Check className="w-8 h-8 text-[#C41230]" data-id="element-108" />
            </div>
            <h2 className="text-2xl font-bold mb-2" data-id="element-109">{t('form.dup.title')}</h2>
            <p className="text-white/60 mb-6" data-id="element-110">{t('form.dup.text')}</p>
            <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="btn-whatsapp text-white py-3.5 px-8 rounded-lg text-sm font-bold flex items-center justify-center gap-2 mx-auto inline-flex" data-id="element-111">
              <MessageCircle className="w-5 h-5" data-id="element-112" />
              {t('form.dup.cta')}
            </a>
          </motion.div>
        </div>
      </section>;
  }
  // ── SUCCESS SCREEN ──
  if (isSuccess) {
    return <section id="lead-form" className="py-16 md:py-24 bg-[#111111] text-white" data-id="element-113">
        <div className="container mx-auto px-5 md:px-6" data-id="element-114">
          <motion.div className="max-w-lg mx-auto bg-white/5 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-white/10 text-center" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} data-id="element-115">
            {/* Success icon with pulse */}
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-5 relative" data-id="element-116">
              <div className="absolute inset-0 bg-emerald-500/10 rounded-full animate-ping" data-id="element-117" />
              <Check className="w-8 h-8 text-emerald-400 relative z-10" data-id="element-118" />
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold mb-1" data-id="element-119">
              {t('form.success.title')} {formData.firstName}
              {t('form.success.titleEnd')}
            </h2>
            <p className="text-white/50 text-sm mb-6" data-id="element-120">
              {t('form.success.subtitle')}
            </p>

            {/* Countdown or "opened" state */}
            {!waOpened ? <div className="mb-6" data-id="element-121">
                <p className="text-white/60 text-sm mb-3" data-id="element-122">
                  {t('form.success.redirect')}{' '}
                  <span className="font-bold text-[#d4a853] text-lg" data-id="element-123">
                    {countdown}
                  </span>{' '}
                  {t('form.success.seconds')}
                </p>
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden max-w-[220px] mx-auto" data-id="element-124">
                  <motion.div className="h-full bg-[#d4a853] rounded-full" initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 7,
                ease: 'linear'
              }} data-id="element-125" />
                </div>
              </div> : null}

            {/* WhatsApp CTA button — icon only */}
            <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" onClick={() => {
            if (!waOpenedRef.current) {
              trackContact('lead-form-whatsapp-manual');
              waOpenedRef.current = true;
              setWaOpened(true);
            }
          }} className="w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30 transition-colors" aria-label="Chat on WhatsApp" data-id="element-126">
              <MessageCircle className="w-7 h-7 text-white" data-id="element-127" />
            </a>

            {/* Fallback if popup was blocked */}
            {showFallback && <motion.div className="mt-5 bg-white/5 rounded-lg p-4 border border-white/10" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-128">
                <p className="text-white/40 text-xs mb-2" data-id="element-129">
                  {t('form.success.fallback')}
                </p>
                <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-sm font-semibold hover:text-emerald-300 transition-colors" data-id="element-130">
                  {t('form.success.fallbackLink')}
                </a>
              </motion.div>}
          </motion.div>
        </div>
      </section>;
  }
  // ── FORM ──
  return <section id="lead-form" className="py-16 md:py-24 bg-[#111111] text-white relative overflow-hidden" data-id="element-131">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(196,18,48,0.1)_0%,_transparent_60%)]" data-id="element-132" />

      <div className="container mx-auto px-5 md:px-6 relative z-10" data-id="element-133">
        <div className="max-w-xl mx-auto" data-id="element-134">
          <div className="text-center mb-6" data-id="element-135">
            <motion.h2 className="text-2xl md:text-4xl font-black mb-2 uppercase tracking-tight" initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-136">
              {t('form.title1')}{' '}
              <span className="text-[#C41230]" data-id="element-137">{t('form.title2')}</span>{' '}
              {t('form.title3')}
            </motion.h2>
            <p className="text-white/40 text-sm" data-id="element-138">{t('form.sub')}</p>
          </div>

          <UrgencyTimer data-id="element-139" />

          <motion.form onSubmit={handleSubmit} className="bg-white/[0.03] backdrop-blur-sm p-5 md:p-8 rounded-2xl border border-white/10" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} data-id="element-140">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-141">
              <div className="space-y-1.5" data-id="element-142">
                <label htmlFor="firstName" className={labelClass} data-id="element-143">
                  {t('form.name')}
                </label>
                <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className={inputClass} placeholder={t('form.name.placeholder')} data-id="element-144" />
              </div>
              <div className="space-y-1.5" data-id="element-145">
                <label htmlFor="phone" className={labelClass} data-id="element-146">
                  {t('form.phone')}
                </label>
                <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} onFocus={() => {
                if (!showExtended) {
                  setShowExtended(true);
                  trackInitiateCheckout();
                }
              }} className={inputClass} placeholder="(305) 123-4567" data-id="element-147" />
              </div>
            </div>

            <AnimatePresence data-id="element-148">
              {showExtended && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.4
            }} className="overflow-hidden" data-id="element-149">
                  <div className="space-y-1.5 mb-4" data-id="element-150">
                    <label htmlFor="email" className={labelClass} data-id="element-151">
                      {t('form.email')}
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder={t('form.email.placeholder')} data-id="element-152" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-5" data-id="element-153">
                    <div className="space-y-1.5" data-id="element-154">
                      <label htmlFor="vehicleInterest" className={labelClass} data-id="element-155">
                        {t('form.vehicle')}
                      </label>
                      <select id="vehicleInterest" name="vehicleInterest" value={formData.vehicleInterest} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-156">
                        <option value="SUV" data-id="element-157">{t('form.vehicle.suv')}</option>
                        <option value="Sedan" data-id="element-158">{t('form.vehicle.sedan')}</option>
                        <option value="Truck" data-id="element-159">{t('form.vehicle.truck')}</option>
                        <option value="Coupe" data-id="element-160">{t('form.vehicle.coupe')}</option>
                        <option value="Other" data-id="element-161">{t('form.vehicle.other')}</option>
                      </select>
                    </div>
                    <div className="space-y-1.5" data-id="element-162">
                      <label htmlFor="creditSituation" className={labelClass} data-id="element-163">
                        {t('form.credit')}
                      </label>
                      <select id="creditSituation" name="creditSituation" value={formData.creditSituation} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-164">
                        <option value="Excellent (720+)" data-id="element-165">
                          {t('form.credit.excellent')}
                        </option>
                        <option value="Good (660-719)" data-id="element-166">
                          {t('form.credit.good')}
                        </option>
                        <option value="Fair (600-659)" data-id="element-167">
                          {t('form.credit.fair')}
                        </option>
                        <option value="Rebuilding (below 600)" data-id="element-168">
                          {t('form.credit.rebuilding')}
                        </option>
                        <option value="First-time buyer" data-id="element-169">
                          {t('form.credit.first')}
                        </option>
                        <option value="Not sure" data-id="element-170">
                          {t('form.credit.unsure')}
                        </option>
                      </select>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <button type="submit" disabled={isSubmitting} className="w-full btn-primary py-3.5 rounded-lg text-sm md:text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-171">
              {isSubmitting ? <>
                  <Loader2 className="w-4 h-4 animate-spin" data-id="element-172" />
                  {t('form.processing')}
                </> : t('form.submit')}
            </button>

            <p className="mt-3 flex items-center justify-center gap-1.5 text-white/30 text-[11px]" data-id="element-173">
              <ShieldCheck className="w-3.5 h-3.5" data-id="element-174" />
              {t('form.secure')}
            </p>
          </motion.form>
        </div>
      </div>
    </section>;
}